import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PencilIcon, SettingsIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { ProfileHero, ProfileHeroSkeleton } from '../../components/profile/ProfileHero';
import { ProfileCardSkeleton } from '../../components/profile/ProfileCard';
import { AboutYouCard, CompletionCard, DataAccountCard, ExperienceCard, PrivacyCard, SecurityCard } from '../../components/profile/ProfileSections';
import { AcademicSnapshot, CampusSnapshot, WalletSnapshot } from '../../components/profile/SnapshotCards';
import { RoleAwareSection } from '../../components/profile/RoleAwareSection';
import { EditFieldDialog } from '../../components/profile/EditFieldDialog';
import { PROFILE_FIELDS } from '../../data/profileConfig';
import { calculateProfileCompletion } from '../../utils/profileCompletion';
import { useAuth } from '../../contexts/AuthContext';
import type { ProfileFieldConfig } from '../../types/profile';

/**
 * My profile.
 *
 * The page composes configured sections; it holds no profile content of its own.
 * Everything shown comes from the authenticated identity, the account record or
 * the domain that owns the figure, so nothing here can present a value the
 * platform does not actually hold.
 */
export function Profile() {
  const {
    identity,
    updateProfile
  } = useAuth();
  const [editing, setEditing] = useState<ProfileFieldConfig | null>(null);
  const [confirm, setConfirm] = useState<'deactivate' | 'delete' | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  // The identity is restored asynchronously from the stored session, so the
  // first paint may genuinely have nothing to show. Skeletons keep the layout.
  if (!identity) {
    return <div className="space-y-5" data-id="element-11578" data-mp="1lpqzyeufptha">
        <ProfileHeroSkeleton data-id="element-11579" data-mp="1lofxcicq6rrr" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-11580" data-mp="1ln4q28k2n1vw">
          <ProfileCardSkeleton rows={2} data-id="element-11581" data-mp="3lqp7q73kowb8" />
          <ProfileCardSkeleton rows={2} data-id="element-11582" data-mp="3qqlc47g6mprb" />
          <ProfileCardSkeleton rows={2} data-id="element-11583" data-mp="3qpjo72e2i8s6" />
          <ProfileCardSkeleton rows={2} data-id="element-11584" data-mp="3qogxaugqw6c9" />
        </div>
      </div>;
  }
  const completion = calculateProfileCompletion(identity, PROFILE_FIELDS, identity.activeRole);
  const openEdit = (field: ProfileFieldConfig) => setEditing(field);
  const openEditById = (id: string) => {
    const field = PROFILE_FIELDS.find(item => item.id === id);
    if (field) setEditing(field);
  };
  const exportData = () => {
    // The same subset the account settings export produces: never password
    // material, and never anything belonging to somebody else.
    const payload = {
      id: identity.id,
      username: identity.username,
      displayName: identity.displayName,
      email: identity.email,
      roles: identity.roles,
      programme: identity.programme,
      level: identity.level,
      bio: identity.bio ?? null,
      interests: identity.interests ?? [],
      skills: identity.skills ?? [],
      lookingFor: identity.lookingFor ?? [],
      languages: identity.languages ?? [],
      location: identity.location ?? null,
      createdAt: identity.createdAt,
      exportedAt: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `campuzon-profile-${identity.username}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setSaved('Your data has been downloaded.');
  };
  return <div className="space-y-5" data-id="element-11585" data-mp="dj93dbc80v8x">
      <PageHeader title="My profile" description="Manage your identity, preferences and account settings." documentTitle actions={<>
            <Button variant="secondary" icon={<PencilIcon className="h-4 w-4" data-id="element-11588" data-mp="23un4jeof533w" />} onClick={() => openEditById(completion.next?.id ?? 'bio')} data-id="element-11587" data-mp="23u503urlrikw">
              Edit profile
            </Button>
            <Link to="/settings" data-id="element-11589" data-mp="2futxorndha9p">
              <Button variant="ghost" icon={<SettingsIcon className="h-4 w-4" data-id="element-11591" data-mp="21wwvnfq5gbnn" />} data-id="element-11590" data-mp="2ftskagivwbot">
                Settings
              </Button>
            </Link>
          </>} data-id="element-11586" data-mp="9gcjzieuwv3x" />

      {saved && <p role="status" className="rounded-xl border border-ok-100 bg-ok-50 px-4 py-3 text-body-sm text-ok-800" data-id="element-11592" data-mp="25ztkho6a9401">
          {saved}
        </p>}

      <ProfileHero onEdit={openEditById} data-id="element-11593" data-mp="1wyllt4zuu15f" />

      {/* Completion beside the three at-a-glance cards: on mobile these stack in
          the order that matters — what to do next, then standing, campus, money. */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-id="element-11594" data-mp="1n5ema0rspfd9">
        <CompletionCard outcome={completion} onComplete={() => completion.next && setEditing(completion.next)} data-id="element-11595" data-mp="19912gkzsh1vm" />
        <AcademicSnapshot data-id="element-11596" data-mp="1e8m08s38346r" />
        <CampusSnapshot data-id="element-11597" data-mp="ukhjl99wz4n9" />
        <WalletSnapshot data-id="element-11598" data-mp="ulslt4npo4c9" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3" data-id="element-11599" data-mp="umu9q9ptslbe">
        <div className="min-w-0" data-id="element-11600" data-mp="pmy5qah4otvu">
          <AboutYouCard identity={identity} role={identity.activeRole} onEdit={openEdit} data-id="element-11601" data-mp="pnr68tphun5j" />
        </div>
        <div className="min-w-0" data-id="element-11602" data-mp="13jl0q7pit9z4">
          <ExperienceCard role={identity.activeRole} data-id="element-11603" data-mp="zkcbng5fe09w" />
        </div>
        <div className="min-w-0 space-y-4" data-id="element-11604" data-mp="zm6sai9nz686">
          <PrivacyCard data-id="element-11605" data-mp="1dj3ichhykjo0" />
          <SecurityCard data-id="element-11606" data-mp="1dialq9cprajl" />
          <DataAccountCard onExport={exportData} onDeactivate={() => setConfirm('deactivate')} onDelete={() => setConfirm('delete')} data-id="element-11607" data-mp="1dhsfcjwc7fxy" />
        </div>
      </div>

      <RoleAwareSection data-id="element-11608" data-mp="1nbhmfgwie2dc" />

      <EditFieldDialog field={editing} identity={identity} open={Boolean(editing)} onClose={() => setEditing(null)} onSave={patch => {
      updateProfile(patch);
      setSaved('Your profile has been updated.');
    }} data-id="element-11609" data-mp="1ia22xt9zc3x2" />

      <Modal open={confirm !== null} onClose={() => setConfirm(null)} title={confirm === 'delete' ? 'Delete your account?' : 'Deactivate your account?'} description={confirm === 'delete' ? 'This cannot be undone. Your profile, roommate data and career profile are removed. Tenancy, ledger and payment records are kept in anonymised form where the law requires it.' : 'Your profile stops being visible to others and you are signed out everywhere. Nothing is deleted, and signing back in restores it.'} footer={<>
            <Button variant="ghost" onClick={() => setConfirm(null)} data-id="element-11611" data-mp="1hmsv6tze96rm">
              Cancel
            </Button>
            <Link to="/support" data-id="element-11612" data-mp="1loedwvym0f9q">
              <Button variant={confirm === 'delete' ? 'danger' : 'primary'} data-id="element-11613" data-mp="1lnl4fn89nbnw">
                Continue in Support
              </Button>
            </Link>
          </>} data-id="element-11610" data-mp="oxub3rbpnujo">
        <p className="text-body-sm text-ink-muted" data-id="element-11614" data-mp="3ngj4o44dh6iv">
          Both of these require identity confirmation, so they are completed with Support rather than
          on a single button here. You will be asked to confirm it is you before anything changes.
        </p>
      </Modal>
    </div>;
}